import React, { useState, useEffect, useMemo } from 'react';
import { Icons } from '../../utils/icons';
import { authService } from '../../services/authService';
import { ESTADO_PUESTA_MARCHA, ORDEN_ESTADOS_OBRA, resolverStelorderId, colorAutomaticoObra } from './utils/estadosObra';

function fmt(fecha) {
  if (!fecha) return '';
  const [y, m, d] = fecha.split('-');
  return `${d}/${m}/${y}`;
}

export default function ObraDetalle({ obra, usuario, onClose, onEditar, onEliminar }) {
  const [clienteQuery, setClienteQuery] = useState('');
  const [clientesTodos, setClientesTodos] = useState(null); // null = aún no cargado
  const [cargandoClientes, setCargandoClientes] = useState(false);
  const [añadiendo, setAñadiendo] = useState(false);
  const [error, setError] = useState('');
  const [actualizandoId, setActualizandoId] = useState(null);
  const [estadosCalculados, setEstadosCalculados] = useState({}); // { stelorderId: 'registrado'|'agendado'|'realizado'|'ext' }

  // Estado calculado automáticamente (ver estadoClientesObra.js): cruza
  // contra los contratos y las órdenes de puesta en marcha sincronizadas
  // desde StelOrder, así no depende de que alguien lo marque a mano.
  useEffect(() => {
    const stelorderIds = (obra.clientes || []).map(resolverStelorderId).filter(Boolean);
    if (stelorderIds.length === 0) { setEstadosCalculados({}); return; }
    let cancelado = false;
    fetch('/.netlify/functions/estadoClientesObra', {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
      body: JSON.stringify({ stelorderIds }),
    })
      .then(r => r.json())
      .then(d => { if (!cancelado && d.success) setEstadosCalculados(d.estados); })
      .catch(() => {});
    return () => { cancelado = true; };
  }, [obra.clientes]);

  const estadoDe = (c) => {
    const sid = resolverStelorderId(c);
    return (sid && estadosCalculados[String(sid)]) || 'registrado';
  };

  // Lista de clientes desde Firebase (colección "clientes") — se carga una
  // vez al abrir el buscador y se filtra en memoria, igual que en el
  // buscador de cliente de las Visitas del calendario.
  useEffect(() => {
    if (clientesTodos !== null || cargandoClientes) return;
    setCargandoClientes(true);
    (async () => {
      try {
        const all = [];
        let cursor = null;
        do {
          const url = `/.netlify/functions/getClientes?soloActivos=true&limit=2000${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
          const r = await fetch(url, { headers: { ...authService.authHeader() } });
          const d = await r.json();
          if (!d.success) break;
          all.push(...d.clientes);
          cursor = d.nextCursor || null;
        } while (cursor);
        setClientesTodos(all);
      } catch { setClientesTodos([]); }
      finally { setCargandoClientes(false); }
    })();
  }, [clientesTodos, cargandoClientes]);

  const clientesEnObra = useMemo(() => new Set((obra.clientes || []).map(c => String(c.clienteId))), [obra.clientes]);

  const resultadosBusqueda = useMemo(() => {
    const q = clienteQuery.trim().toLowerCase();
    if (q.length < 2 || !clientesTodos) return [];
    const qNum = q.replace(/\s/g, '');
    return clientesTodos.filter(c =>
      !clientesEnObra.has(String(c.id)) && (
        (c.nombre       || '').toLowerCase().includes(q) ||
        (c.nombreFiscal || '').toLowerCase().includes(q) ||
        (c.email        || '').toLowerCase().includes(q) ||
        (c.telefono     || '').replace(/\s/g, '').includes(qNum) ||
        (c.movil        || '').replace(/\s/g, '').includes(qNum)
      )
    ).slice(0, 8);
  }, [clientesTodos, clienteQuery, clientesEnObra]);

  async function añadirCliente(c) {
    setAñadiendo(true); setError('');
    try {
      const resp = await fetch('/.netlify/functions/manageObras', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          action: 'addCliente', id: obra.id, clienteId: c.id, clienteStelorderId: c.stelorderId,
          clienteNombre: c.nombre, clienteTelefono: c.telefono || c.movil || '', clienteDireccion: c.direccion,
          // Ya está en su ficha de StelOrder — se copia aquí para el
          // seguimiento comercial tras la puesta en marcha (funnelObras.js).
          clienteEmail: c.email || '',
        }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      setClienteQuery('');
    } catch (e) { setError(e.message); }
    finally { setAñadiendo(false); }
  }

  async function quitarCliente(clienteId) {
    if (!window.confirm('¿Quitar este cliente de la obra?')) return;
    setActualizandoId(clienteId); setError('');
    try {
      const resp = await fetch('/.netlify/functions/manageObras', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ action: 'quitarCliente', id: obra.id, clienteId }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
    } catch (e) { setError(e.message); }
    finally { setActualizandoId(null); }
  }

  // Aviso suave (no bloquea añadir el cliente a la obra): sin email no le
  // llega el seguimiento comercial tras la puesta en marcha (funnelObras.js).
  async function añadirEmailCliente(c) {
    const email = window.prompt(`Email de ${c.clienteNombre}:`, c.clienteEmail || '');
    if (email === null) return; // cancelado
    setActualizandoId(c.clienteId); setError('');
    try {
      const resp = await fetch('/.netlify/functions/manageObras', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ action: 'actualizarCliente', id: obra.id, clienteId: c.clienteId, clienteEmail: email }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
    } catch (e) { setError(e.message); }
    finally { setActualizandoId(null); }
  }

  const clientes = obra.clientes || [];
  const conteos = ORDEN_ESTADOS_OBRA.reduce((acc, e) => ({ ...acc, [e]: clientes.filter(c => estadoDe(c) === e).length }), {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: colorAutomaticoObra(clientes, estadoDe) }} />
            <h3 className="font-bold text-gray-900">{obra.titulo}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {obra.fecha && <p className="text-gray-700"><span className="font-bold text-gray-400">Fecha:</span> {fmt(obra.fecha)}</p>}
            {(obra.contactoNombre || obra.contactoTelefono) && (
              <p className="text-gray-700"><span className="font-bold text-gray-400">Contacto:</span> {[obra.contactoNombre, obra.contactoTelefono].filter(Boolean).join(' · ')}</p>
            )}
            {obra.direccion && <p className="text-gray-700"><span className="font-bold text-gray-400">Dirección:</span> {obra.direccion}</p>}
            {obra.constructor && <p className="text-gray-700"><span className="font-bold text-gray-400">Constructor:</span> {obra.constructor}</p>}
            {obra.promotor && <p className="text-gray-700"><span className="font-bold text-gray-400">Promotor:</span> {obra.promotor}</p>}
            {obra.instalador && <p className="text-gray-700"><span className="font-bold text-gray-400">Instalador:</span> {obra.instalador}</p>}
          </div>

          {obra.observaciones && (
            <div className="bg-gray-50 rounded-xl p-3 text-sm">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Observaciones</p>
              <p className="text-gray-700 whitespace-pre-wrap">{obra.observaciones}</p>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {ORDEN_ESTADOS_OBRA.map(e => (
              <span key={e} className={`text-xs font-bold px-2.5 py-1 rounded-full ${ESTADO_PUESTA_MARCHA[e].bg} ${ESTADO_PUESTA_MARCHA[e].text}`}>
                {conteos[e]} {ESTADO_PUESTA_MARCHA[e].label}
              </span>
            ))}
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5">Añadir cliente</label>
            <div className="relative">
              <input value={clienteQuery} onChange={e => setClienteQuery(e.target.value)}
                placeholder="Buscar cliente por nombre o teléfono..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400" />
              {cargandoClientes && <p className="text-xs text-gray-400 mt-1">Cargando listado de clientes...</p>}
              {resultadosBusqueda.length > 0 && (
                <div className="mt-1 border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                  {resultadosBusqueda.map(c => (
                    <button key={c.id} type="button" disabled={añadiendo} onClick={() => añadirCliente(c)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50">
                      <p className="font-bold text-gray-800">{c.nombre}</p>
                      {(c.telefono || c.movil) && <p className="text-xs text-gray-500">{c.telefono || c.movil}</p>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5">Clientes de la obra ({clientes.length})</label>
            {clientes.length === 0 ? (
              <p className="text-sm text-gray-400">Todavía no hay clientes añadidos a esta obra.</p>
            ) : (
              <div className="space-y-2">
                {clientes.map(c => {
                  const estado = estadoDe(c);
                  const cfg = ESTADO_PUESTA_MARCHA[estado];
                  return (
                    <div key={c.clienteId} className={`flex items-center justify-between gap-2 bg-gray-50 rounded-xl px-3 py-2 border-l-4 ${cfg.border}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">{c.clienteNombre}</p>
                        {c.clienteTelefono && <p className="text-xs text-gray-500">{c.clienteTelefono}</p>}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {!c.clienteEmail && (
                          <button onClick={() => añadirEmailCliente(c)} disabled={actualizandoId === c.clienteId}
                            title="Sin email — no recibirá el seguimiento comercial tras la puesta en marcha. Haz clic para añadirlo."
                            className="text-xs font-bold rounded-full px-2.5 py-1 bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50">
                            ⚠️ Sin email
                          </button>
                        )}
                        <span className={`text-xs font-bold rounded-full px-2.5 py-1 ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                        <button onClick={() => quitarCliente(c.clienteId)} disabled={actualizandoId === c.clienteId}
                          className="text-gray-400 hover:text-red-600 p-1 disabled:opacity-50">
                          <Icons.Trash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl p-3">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-between gap-2">
          <button onClick={onEliminar}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors">
            <Icons.Trash className="w-4 h-4" />Eliminar obra
          </button>
          <button onClick={onEditar}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
            <Icons.Edit className="w-4 h-4" />Editar obra
          </button>
        </div>
      </div>
    </div>
  );
}
